import { useMunicipalityStore } from "../../store";
import { RequestMunicipality } from "interfaces";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getDefaultValueForSelect,
  validateSelect,
  validateYupCodeWithMinMaxLeght,
  validateYupStringWithSpecialCharacters,
} from "helpers/functionsHelpers";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { CreateMunicipality, FetchDepartament, UpdateMunicipality } from "services";
import { OptionSelect } from "Common/Components";
import { NotificationType, showNotifications } from "helpers/toas_notification";
import { useMunicipalityList } from "../useMunicipalityList";
import { useEffect, useState } from "react";

export const useModalMunicipality = () => {
  const {
    clearAll,
    isEdit,
    isOpenModal,
    setIsEdit,
    setMunicipality,
    setIsOpenModal,
    municipality,
  } = useMunicipalityStore();
  const {refetchMunicipality}= useMunicipalityList()
  const { t } = useTranslation();
  const [valueDepartment, setValueDepartment] = useState<OptionSelect>();

  const initialValues: RequestMunicipality = {
    description: municipality?.description || "",
    code: municipality?.code || "",
    department_id: municipality?.departament.id || 0,
  };

  const validationSchema = Yup.object({
    description: validateYupStringWithSpecialCharacters(t, false),
    code: validateYupCodeWithMinMaxLeght(t, 5, 5, 'numbers'),
    department_id: validateSelect(t),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        if(isEdit && municipality?.id){
          await updateMunicipality(municipality.id, values)
        }else {
          await createMunicipality(values)
        }
      } catch (error) {
        throw error;
      } finally {
        setSubmitting(false);
        formik.resetForm()
      }
    },
  });

  const { data: departamentData } = useQuery({
    queryKey: ["departament"],
    queryFn: async () => {
      try {
        const response = await FetchDepartament();
        if (!response?.data?.length) return [];
        
        return response.data.map((dept): OptionSelect => ({
          label: dept.description,
          value: dept.id.toString(),
        }));
      } catch {
        return [];
      }
    },
    staleTime: 5000,
  });

  useEffect(() => {
    if (formik.values.department_id) {
      const defaultValueFind = getDefaultValueForSelect(
        departamentData || [],
        formik.values.department_id.toString()
      );
      setValueDepartment(defaultValueFind);
    }
  }, [formik.values.department_id, departamentData]);

  const createMunicipality = async (data: RequestMunicipality) => {
    try {
      
      const values : RequestMunicipality={
        description: data.description,
        code: data.code,
        department_id: Number(data.department_id)
      }
      const response = await CreateMunicipality(values);
      
      if (response.code === "200") {
        setIsOpenModal(false);
        refetchMunicipality()
        showNotifications(
          NotificationType.SUCCESS,
          t("notificationsMessages.successAdd")
        );
        clearAll();
      } else {
        clearAll();
        showNotifications(
          NotificationType.ERROR,
          t("notificationsMessages.error") + response.message
        );
      }
    } catch (error) {
      throw error;
    }
  };

  const updateMunicipality = async (id: number, data: RequestMunicipality) => {
    try {
      const values : RequestMunicipality={
        description: data.description,
        code: data.code,
        department_id: Number(data.department_id)
      }
      
      const response = await UpdateMunicipality(id,values);
      
      if (response.code === "200") {
        setIsOpenModal(false);
        refetchMunicipality()
        showNotifications(
          NotificationType.SUCCESS,
          t("notificationsMessages.success")
        );
        clearAll();
      } else {
        clearAll();
        showNotifications(
          NotificationType.ERROR,
          t("notificationsMessages.error") + response.message
        );
      }
    } catch (error) {
      throw error;
    }
  };


  return {
    formik,
    clearAll,
    isEdit,
    isOpenModal,
    setIsEdit,
    setIsOpenModal,
    setMunicipality,
    t,
    valueDepartment,
    departament: departamentData,
  };
};
